import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function GroupModal({ open, onOpenChange, group, onSaved }) {
  const queryClient = useQueryClient();
  const isEdit = !!group;
  const [form, setForm] = useState({
    name: "", group_type: "free", organizer_name: "", organizer_phone: "",
    monthly_fee: 0, notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (group) {
        setForm({
          name: group.name || "",
          group_type: group.group_type || "free",
          organizer_name: group.organizer_name || "",
          organizer_phone: group.organizer_phone || "",
          monthly_fee: group.monthly_fee || 0,
          notes: group.notes || "",
        });
      } else {
        setForm({ name: "", group_type: "free", organizer_name: "", organizer_phone: "", monthly_fee: 0, notes: "" });
      }
    }
  }, [open, group]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Vui lòng nhập tên hội"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/groups/${group.id}`, form);
        toast.success("Đã cập nhật hội");
      } else {
        await api.post("/groups", form);
        toast.success("Đã tạo hội mới");
      }
      // Invalidate cache Lịch sân + stats vì tên hội (customer_name) đã cascade xuống bookings
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Có lỗi xảy ra");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-md" data-testid="group-modal">
        <DialogHeader>
          <DialogTitle className="text-[#F8FAFC] font-heading">
            {isEdit ? "Sửa hội" : "Tạo hội mới"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Group Type Toggle (only when creating) */}
          {!isEdit && (
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Loại hội</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setField("group_type", "free")}
                  className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors border ${form.group_type === "free" ? "bg-lime-500/20 text-lime-400 border-lime-500/30" : "bg-transparent text-slate-400 border-[#334155] hover:border-slate-400"}`}
                  data-testid="type-free-btn">
                  Hội tự do
                </button>
                <button type="button" onClick={() => setField("group_type", "managed")}
                  className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors border ${form.group_type === "managed" ? "bg-sky-500/20 text-sky-400 border-sky-500/30" : "bg-transparent text-slate-400 border-[#334155] hover:border-slate-400"}`}
                  data-testid="type-managed-btn">
                  Hội quản lý
                </button>
              </div>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Tên hội *</label>
            <Input value={form.name} onChange={(e) => setField("name", e.target.value)}
              placeholder="Hội Pickleball Sáng" className="bg-[#0f172a] border-[#334155] text-slate-200"
              data-testid="group-name-input" />
          </div>

          {/* Organizer (free groups) */}
          {form.group_type === "free" && (
            <>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Người tổ chức</label>
                <Input value={form.organizer_name} onChange={(e) => setField("organizer_name", e.target.value)}
                  placeholder="Nguyễn Văn A" className="bg-[#0f172a] border-[#334155] text-slate-200" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">SĐT tổ chức</label>
                <Input value={form.organizer_phone} onChange={(e) => setField("organizer_phone", e.target.value)}
                  placeholder="0901234567" className="bg-[#0f172a] border-[#334155] text-slate-200" />
              </div>
            </>
          )}

          {/* Monthly Fee */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Phí hàng tháng (VNĐ)</label>
            <Input type="number" value={form.monthly_fee} onChange={(e) => setField("monthly_fee", parseInt(e.target.value) || 0)}
              placeholder="0" className="bg-[#0f172a] border-[#334155] text-slate-200" />
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Ghi chú</label>
            <Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)}
              placeholder="Ghi chú về hội..." className="bg-[#0f172a] border-[#334155] text-slate-200 min-h-[60px]" />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400">Hủy</Button>
            <Button type="submit" disabled={saving} className="bg-lime-500 hover:bg-lime-600 text-[#0f172a] font-semibold" data-testid="group-save-btn">
              {saving ? "Đang lưu..." : (isEdit ? "Cập nhật" : "Tạo hội")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
